import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { updateProfileRequest } from '../../services/authService';

export default function ProfileModal({ open, onClose, onUpdated }) {
  const { user, refreshUser } = useAuth();

  const [fullName, setFullName] = useState(user?.name || '');
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [profileError, setProfileError] = useState('');

  useEffect(() => {
    if (!open) return;
    setFullName(user?.name || '');
    setProfileError('');
  }, [open, user?.name]);

  if (!open || typeof document === 'undefined') return null;

  const handleClose = () => {
    onClose?.();
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setProfileError('');

    const trimmedName = fullName.trim();
    if (!trimmedName) {
      setProfileError('Name is required.');
      return;
    }

    setProfileSubmitting(true);
    try {
      await updateProfileRequest(trimmedName);
      await refreshUser();
      onUpdated?.('Profile updated successfully.');
      onClose?.();
    } catch (err) {
      setProfileError(err.message || 'Could not update profile.');
    } finally {
      setProfileSubmitting(false);
    }
  };

  return createPortal(
    <div className="admin-dashboard-overlay" onClick={handleClose} role="presentation">
      <div
        className="profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="dashboard-close"
          onClick={handleClose}
          aria-label="Close profile dialog"
        >
          ×
        </button>

        <div className="dashboard-header">
          <span className="dashboard-kicker">Account</span>
          <h2 id="profile-modal-title" className="dashboard-title">
            Profile
          </h2>
          <p className="dashboard-subtitle">Update your display name.</p>
        </div>

        <div className="profile-grid">
          <form className="profile-card" onSubmit={handleProfileSubmit}>
            <h3 className="profile-card-title">Update profile</h3>

            {profileError ? (
              <div className="add-user-alert add-user-alert--error">{profileError}</div>
            ) : null}

            <label className="profile-label" htmlFor="profile-name">
              Name
            </label>
            <input
              id="profile-name"
              className="profile-input"
              type="text"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              autoComplete="name"
              disabled={profileSubmitting}
              required
            />

            <label className="profile-label" htmlFor="profile-email">
              Email
            </label>
            <input
              id="profile-email"
              className="profile-input profile-input--readonly"
              type="email"
              value={user?.email || ''}
              disabled
              readOnly
            />
            <span className="profile-hint">Email cannot be changed.</span>

            <div className="profile-actions">
              <button
                type="submit"
                className="add-user-btn add-user-btn--primary"
                disabled={profileSubmitting}
              >
                {profileSubmitting ? 'Updating…' : 'Update Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
